// =======================================================
// DriftField — slow, quiet ambient particles with twinkle
// =======================================================
//
// Similar architecture to ParticleStorm but visually: fewer particles,
// slower motion, subtler links, per-particle twinkle via phase.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  uniform float uSize;
  attribute float aAlpha;
  attribute float aPhase;
  uniform float uTime;
  uniform float uTwinkle;
  varying float vAlpha;
  void main() {
    float tw = 1.0 - uTwinkle + uTwinkle * (0.5 + 0.5 * sin(uTime * 1.2 + aPhase * 6.28));
    vAlpha = aAlpha * tw;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uSize;
  }
`;
const FRAG = /* glsl */ `
  uniform vec3 uColor;
  varying float vAlpha;
  void main() {
    vec2 p = gl_PointCoord * 2.0 - 1.0;
    float d = length(p);
    float a = smoothstep(1.0, 0.0, d) * vAlpha;
    if (a < 0.01) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

export class DriftField extends Generator {
  constructor() {
    super({
      name: 'Drift Field',
      category: 'ambient',
      schema: {
        count:     { default: 250, min: 20,  max: 1200, step: 10,   label: 'Count' },
        speed:     { default: 16,  min: 0,   max: 120,  step: 0.5,  label: 'Drift speed' },
        size:      { default: 4,   min: 1,   max: 14,   step: 0.5,  label: 'Size' },
        link:      { default: 140, min: 0,   max: 500,  step: 1,    label: 'Link range (px)' },
        linkAlpha: { default: 0.2, min: 0,   max: 1,    step: 0.01, label: 'Link alpha' },
        twinkle:   { default: 0.35, min: 0,  max: 1,    step: 0.01, label: 'Twinkle' },
        accent:    { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
    this._maxCount = 1200;
    this._linkCap  = 1500;
  }

  onEnter(scene, engine) {
    this.engine = engine;

    const geo = new THREE.BufferGeometry();
    const posArr   = new Float32Array(this._maxCount * 3);
    const alphaArr = new Float32Array(this._maxCount);
    const phaseArr = new Float32Array(this._maxCount);
    geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    geo.setAttribute('aAlpha',  new THREE.BufferAttribute(alphaArr, 1));
    geo.setAttribute('aPhase',  new THREE.BufferAttribute(phaseArr, 1));
    this.posArr = posArr;
    this.alphaArr = alphaArr;
    this.phaseArr = phaseArr;

    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uColor:   { value: new THREE.Color(this.v('accent')) },
        uSize:    { value: this.v('size') },
        uTime:    { value: 0 },
        uTwinkle: { value: this.v('twinkle') },
      },
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
    scene.add(this.points);
    this.track(this.points);

    // link mesh
    const linkGeo = new THREE.BufferGeometry();
    const lPos = new Float32Array(this._linkCap * 2 * 3);
    const lA   = new Float32Array(this._linkCap * 2);
    linkGeo.setAttribute('position', new THREE.BufferAttribute(lPos, 3));
    linkGeo.setAttribute('aAlpha',   new THREE.BufferAttribute(lA, 1));
    linkGeo.setDrawRange(0, 0);
    this.linkGeo = linkGeo;
    this.linkPos = lPos;
    this.linkA   = lA;
    const linkMat = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute float aAlpha;
        varying float vA;
        void main() {
          vA = aAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying float vA;
        void main() { gl_FragColor = vec4(uColor, vA); }`,
      uniforms: { uColor: { value: new THREE.Color(this.v('accent')) } },
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending
    });
    this.lines = new THREE.LineSegments(linkGeo, linkMat);
    this.lines.frustumCulled = false;
    scene.add(this.lines);
    this.track(this.lines);

    this.particles = [];
    this._seedParticles();
  }

  _seedParticles() {
    const aspect = this.engine ? this.engine.width / this.engine.height : 2;
    this.particles = [];
    for (let i = 0; i < this._maxCount; i++) {
      this.particles.push({
        x0: this.randRange(200 + i * 8, -aspect, aspect),
        y0: this.randRange(201 + i * 8, -1, 1),
        ampX: this.randRange(202 + i * 8, 0.02, 0.18) * aspect,
        ampY: this.randRange(203 + i * 8, 0.02, 0.14),
        freqX: Math.max(1, Math.floor(this.randRange(204 + i * 8, 1, 5))),
        freqY: Math.max(1, Math.floor(this.randRange(205 + i * 8, 1, 5))),
        a: this.randRange(206 + i * 8, 0.35, 1.0),
        phase: this.rand01(207 + i * 8)
      });
    }
  }

  reseed() { this._seedParticles(); }

  onParamChange(key, val) {
    if (!this.points) return;
    if (key === 'accent') {
      this.points.material.uniforms.uColor.value.set(val);
      this.lines.material.uniforms.uColor.value.set(val);
    } else if (key === 'size')    this.points.material.uniforms.uSize.value = val;
    else if (key === 'twinkle') this.points.material.uniforms.uTwinkle.value = val;
  }

  update(dt, time, engine) {
    const v = this.values;
    const aspect = engine.width / engine.height;
    const pxToNdcX = 2 * aspect / engine.width;
    const count = Math.min(Math.floor(v.count), this._maxCount);
    const loop = engine.getLoopInfo(time);

    this.points.material.uniforms.uTime.value = loop.phase * loop.duration;

    for (let i = 0; i < count; i++) {
      const p = this.particles[i];
      const ang = (loop.phase * v.speed * 0.15 + p.phase) * Math.PI * 2;
      const x = p.x0 + Math.sin(ang * p.freqX) * p.ampX;
      const y = p.y0 + Math.cos(ang * p.freqY) * p.ampY;
      const idx = i * 3;
      this.posArr[idx]     = Math.max(-aspect, Math.min(aspect, x));
      this.posArr[idx + 1] = Math.max(-1, Math.min(1, y));
      this.posArr[idx + 2] = 0;
      this.alphaArr[i] = p.a;
      this.phaseArr[i] = p.phase;
    }
    for (let i = count; i < this._maxCount; i++) this.alphaArr[i] = 0;

    this.points.geometry.attributes.position.needsUpdate = true;
    this.points.geometry.attributes.aAlpha.needsUpdate = true;
    this.points.geometry.attributes.aPhase.needsUpdate = true;
    this.points.geometry.setDrawRange(0, count);

    // links (O(n²) since count is small for ambient; simpler than hash)
    let linkCount = 0;
    if (v.link > 0 && v.linkAlpha > 0) {
      const linkNdc = v.link * pxToNdcX;
      const linkSq = linkNdc * linkNdc;
      const cap = this._linkCap;
      for (let i = 0; i < count && linkCount < cap; i++) {
        const a = this.particles[i];
        for (let j = i + 1; j < count && linkCount < cap; j++) {
          const b = this.particles[j];
          const ax = this.posArr[i * 3], ay = this.posArr[i * 3 + 1];
          const bx = this.posArr[j * 3], by = this.posArr[j * 3 + 1];
          const dx = ax - bx, dy = ay - by;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkSq) {
            const al = (1 - d2 / linkSq) * v.linkAlpha;
            const base = linkCount * 6;
            this.linkPos[base]     = ax;
            this.linkPos[base + 1] = ay;
            this.linkPos[base + 2] = 0;
            this.linkPos[base + 3] = bx;
            this.linkPos[base + 4] = by;
            this.linkPos[base + 5] = 0;
            this.linkA[linkCount * 2]     = al;
            this.linkA[linkCount * 2 + 1] = al;
            linkCount++;
          }
        }
      }
    }
    this.linkGeo.attributes.position.needsUpdate = true;
    this.linkGeo.attributes.aAlpha.needsUpdate = true;
    this.linkGeo.setDrawRange(0, linkCount * 2);
  }
}
